import { create } from "zustand";

import { applyFavicon } from "@/brand/favicon";
import { appShadcnVars, appThemeVars, DEFAULT_THEME_ID, logoVars, themeById, type LogoTheme } from "@/brand/themes";

const KEY = "gv:logoTheme";
const MIGRATED_KEY = "gv:logoTheme:obsidianMigrated";

function loadId(): string {
  try {
    let stored = localStorage.getItem(KEY);
    if (!localStorage.getItem(MIGRATED_KEY)) {
      // One-time: the pre-obsidian default was "brass" — never a deliberate
      // choice, just the old default. Anyone still on it (or with nothing
      // stored) adopts the new "obsidian" default, so existing users keep the
      // violet accent they already saw in the shell chrome. The flag makes this
      // run exactly once, so a *future* deliberate "brass" pick still sticks.
      if (stored === null || stored === "brass") stored = DEFAULT_THEME_ID;
      try {
        localStorage.setItem(MIGRATED_KEY, "1");
        localStorage.setItem(KEY, stored);
      } catch {
        /* private mode — non-fatal */
      }
    }
    return stored ?? DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

/** Apply a theme to the whole app: the `--logo-*` variables (the inline logo
 * animates the swap via its `fill` transition), the `--gv-*` channel variables
 * that drive every legacy Tailwind colour, the shadcn `--primary`/surface HSL
 * channels that drive the shell chrome + shared primitives + dock, and the
 * re-rasterised favicon. Both token systems now recolour together. */
export function applyLogoTheme(t: LogoTheme): void {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(logoVars(t))) root.style.setProperty(k, v);
  for (const [k, v] of Object.entries(appThemeVars(t))) root.style.setProperty(k, v);
  for (const [k, v] of Object.entries(appShadcnVars(t))) root.style.setProperty(k, v);
  void applyFavicon(t);
}

interface ThemeState {
  id: string;
  setTheme: (id: string) => void;
}

export const useTheme = create<ThemeState>((set) => ({
  id: loadId(),
  setTheme: (id) => {
    const t = themeById(id);
    try {
      localStorage.setItem(KEY, id);
    } catch {
      /* private mode — non-fatal */
    }
    applyLogoTheme(t);
    set({ id });
  },
}));

// Apply the stored theme once at startup, before first paint of the logo.
applyLogoTheme(themeById(loadId()));
